import { useState, useEffect, Fragment, useMemo } from 'react';
import { Alert, Badge, Button, Spinner, Toast, ToastContainer } from 'react-bootstrap';
import Loader from '../components/loader';
import TableHeader from '../components/table/header';
import TableFooter from '../components/table/footer';
import {
    getBooleanBadge,
    translateBoolean,
    translateModule,
    translateProtocol,
    translateStatus,
    translateTaskStatus,
    translateUninstallReason,
} from './helpers';
import axios from 'axios';

const headers = {
    Accept: 'application/json',
    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content'),
    Authorization: 'Bearer ' + document.querySelector('meta[name="api-token"]')?.getAttribute('content'),
};

const capitalize = (value = '') => {
    if (!value) {
        return '';
    }

    return value.charAt(0).toUpperCase() + value.slice(1);
};

const prettifyValue = (value = '') => {
    if (value === null || value === undefined || value === '') {
        return '';
    }

    return translateStatus(value);
};

const formatTimestamp = (value) => {
    if (!value) {
        return '';
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return '';
    }

    return date.toLocaleString();
};

const useDebouncedValue = (value, delay = 350) => {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const timeout = window.setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            window.clearTimeout(timeout);
        };
    }, [value, delay]);

    return debouncedValue;
};

const normalizeResponse = (responseData) => {
    if (Array.isArray(responseData)) {
        return {
            data: responseData,
            total: responseData.length,
            page: 1,
            pageSize: responseData.length || 10,
        };
    }

    return {
        data: responseData?.data || [],
        total: responseData?.total ?? responseData?.meta?.total ?? 0,
        page: responseData?.current_page ?? responseData?.page ?? responseData?.meta?.current_page ?? 1,
        pageSize: responseData?.per_page ?? responseData?.page_size ?? responseData?.meta?.per_page ?? 10,
    };
};

const ClipboardIcon = ({ checked = false }) => (
    <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
    >
        {checked ? (
            <path d="M20 6 9 17l-5-5" />
        ) : (
            <>
                <rect width="8" height="4" x="8" y="2" rx="1" />
                <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
            </>
        )}
    </svg>
);

const ShieldIcon = () => (
    <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
    >
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
        <path d="m9 12 2 2 4-4" />
    </svg>
);

const PowerIcon = () => (
    <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
    >
        <path d="M12 2v10" />
        <path d="M18.4 6.6a9 9 0 1 1-12.8 0" />
    </svg>
);

const ListIcon = () => (
    <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
    >
        <path d="M8 6h13" />
        <path d="M8 12h13" />
        <path d="M8 18h13" />
        <path d="M3 6h.01" />
        <path d="M3 12h.01" />
        <path d="M3 18h.01" />
    </svg>
);

const TrashIcon = () => (
    <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
    >
        <path d="M3 6h18" />
        <path d="M8 6V4h8v2" />
        <path d="M19 6l-1 14H6L5 6" />
        <path d="M10 11v6" />
        <path d="M14 11v6" />
    </svg>
);

const NotificationsComponent = () => {
    const [notifications, setNotifications] = useState([]);
    const [filterOptions, setFilterOptions] = useState({
        companies: [],
        modules: [],
        statuses: [],
    });

    const [filters, setFilters] = useState({});
    const debouncedFilters = useDebouncedValue(filters, 350);

    const [sortBy, setSortBy] = useState('created_at');
    const [sortDir, setSortDir] = useState('desc');

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalItems, setTotalItems] = useState(0);

    const [loading, setLoading] = useState(true);
    const [tableLoading, setTableLoading] = useState(false);
    const [error, setError] = useState('');

    const [showDetails, setShowDetails] = useState(null);
    const [copiedJsonId, setCopiedJsonId] = useState(null);
    const [actionLoadingKey, setActionLoadingKey] = useState(null);
    const [actionError, setActionError] = useState('');
    const [quarantineItemsByNotificationId, setQuarantineItemsByNotificationId] = useState({});
    const [toast, setToast] = useState({
        show: false,
        variant: 'success',
        title: '',
        message: '',
    });

    const columns = useMemo(() => {
        return [
            {
                key: 'company_name',
                label: 'Bedrijf',
                filter: {
                    type: 'multiSelect',
                    options: filterOptions.companies,
                },
            },
            {
                key: 'module',
                label: 'Module',
                filter: {
                    type: 'multiSelect',
                    options: filterOptions.modules,
                },
            },
            {
                key: 'computer_name',
                label: 'Computer',
                filter: {
                    type: 'text',
                },
            },
            {
                key: 'status',
                label: 'Status',
                filter: {
                    type: 'select',
                    options: filterOptions.statuses,
                },
            },
            {
                key: 'created_at',
                label: 'Datum',
                filter: {
                    type: 'dateRange',
                },
            },
            {
                key: 'actions',
                label: 'Acties',
                sortable: false,
            },
        ];
    }, [filterOptions]);

    const buildParams = () => {
        const params = {
            page,
            page_size: pageSize,
            sort_by: sortBy,
            sort_dir: sortDir,
        };

        Object.entries(debouncedFilters || {}).forEach(([key, value]) => {
            if (value === undefined || value === null || value === '') {
                return;
            }

            if (Array.isArray(value)) {
                if (value.length > 0) {
                    params[`filter[${key}]`] = value.join(',');
                }

                return;
            }

            if (typeof value === 'object') {
                Object.entries(value).forEach(([rangeKey, rangeValue]) => {
                    if (rangeValue !== undefined && rangeValue !== null && rangeValue !== '') {
                        params[`filter[${key}][${rangeKey}]`] = rangeValue;
                    }
                });

                return;
            }

            params[`filter[${key}]`] = value;
        });

        return params;
    };

    const loadNotifications = async ({ initialLoad = false } = {}) => {
        if (initialLoad) {
            setLoading(true);
        } else {
            setTableLoading(true);
        }

        setError('');

        try {
            const response = await axios.get('/api/v1/notifications', {
                headers,
                params: buildParams(),
            });

            const normalized = normalizeResponse(response.data);

            setNotifications(normalized.data);
            setTotalItems(Number(normalized.total) || 0);
        } catch (error) {
            console.error(error);
            setError('Notificaties konden niet worden opgehaald.');
            setNotifications([]);
            setTotalItems(0);
        } finally {
            setLoading(false);
            setTableLoading(false);
        }
    };

    const loadFilterOptions = async () => {
        try {
            const response = await axios.get('/api/v1/notifications/filter-options', {
                headers,
            });

            setFilterOptions({
                companies: response.data?.companies || [],
                modules: response.data?.modules || [],
                statuses: response.data?.statuses || [],
            });
        } catch (error) {
            console.error(error);

            setFilterOptions({
                companies: [],
                modules: [],
                statuses: [],
            });
        }
    };

    useEffect(() => {
        loadFilterOptions();
    }, []);

    useEffect(() => {
        loadNotifications({ initialLoad: true });
    }, []);

    useEffect(() => {
        setPage(1);
    }, [debouncedFilters, pageSize]);

    useEffect(() => {
        loadNotifications();
    }, [page, pageSize, sortBy, sortDir, debouncedFilters]);

    const handleSort = (key) => {
        if (sortBy !== key) {
            setSortBy(key);
            setSortDir('asc');
            setPage(1);
            return;
        }

        if (sortDir === 'asc') {
            setSortDir('desc');
            setPage(1);
            return;
        }

        setSortBy('created_at');
        setSortDir('desc');
        setPage(1);
    };

    const handlePageSizeChange = (newPageSize) => {
        setPageSize(newPageSize);
        setPage(1);
    };

    const parseEventResponse = (notification) => {
        try {
            if (typeof notification.event_response === 'string') {
                return JSON.parse(notification.event_response);
            }

            return notification.event_response ?? {};
        } catch {
            return {
                raw: String(notification.event_response ?? ''),
            };
        }
    };

    const copyJsonToClipboard = async (notification, eventResponse) => {
        const text = JSON.stringify(eventResponse ?? {}, null, 2);

        try {
            await navigator.clipboard.writeText(text);
            setCopiedJsonId(notification.id);

            window.setTimeout(() => {
                setCopiedJsonId((current) => current === notification.id ? null : current);
            }, 1600);
        } catch (error) {
            console.error(error);
        }
    };

    const getEndpointIdForQuarantine = (notification, eventResponse) => {
        return getEndpointIdForAction(notification, eventResponse);
    };

    const getFilePathForQuarantine = (eventResponse) => {
        return getFilePathForAction(eventResponse);
    };

    const canQuarantineFile = (notification, eventResponse) => {
        const module = eventResponse.module || notification.module;

        const supportedModules = [
            'av',
            'avc',
            'new-incident',
        ];

        return supportedModules.includes(module)
            && Boolean(getEndpointIdForQuarantine(notification, eventResponse))
            && Boolean(getFilePathForQuarantine(eventResponse));
    };

    const getLatestQuarantineAction = (notification) => {
        const actions = Array.isArray(notification.actions) ? notification.actions : [];

        return actions.find((action) => action.type === 'quarantine_file') || null;
    };

    const updateNotificationAction = (notificationId, action) => {
        setNotifications((currentNotifications) =>
            currentNotifications.map((notification) => {
                if (notification.id !== notificationId) {
                    return notification;
                }

                const currentActions = Array.isArray(notification.actions)
                    ? notification.actions.filter((item) => item.id !== action.id)
                    : [];

                return {
                    ...notification,
                    actions: [
                        action,
                        ...currentActions,
                    ],
                };
            })
        );
    };

    const showCommandToast = ({ variant = 'success', title = '', message = '' }) => {
        setToast({
            show: true,
            variant,
            title,
            message,
        });
    };

    const getLatestAction = (notification, type) => {
        const actions = Array.isArray(notification.actions) ? notification.actions : [];

        return actions.find((action) => action.type === type) || null;
    };

    const getEndpointIdForAction = (notification, eventResponse) => {
        return eventResponse.endpointId
            || eventResponse.endpoint_id
            || eventResponse.computer_id
            || notification.computer_id
            || null;
    };

    const getFilePathForAction = (eventResponse) => {
        return eventResponse.file_path
            || eventResponse.exploit_path
            || eventResponse.process_path
            || eventResponse.details?.file?.path
            || eventResponse.details?.name
            || null;
    };

    const extractQuarantineItemsFromResponse = (responseData) => {
        const items = responseData?.items
            || responseData?.response?.result?.items
            || responseData?.response?.result?.quarantineItems
            || responseData?.response?.result
            || responseData?.action?.response_payload?.result?.items
            || responseData?.action?.response_payload?.result?.quarantineItems
            || responseData?.action?.response_payload?.result
            || [];

        return Array.isArray(items) ? items : [];
    };

    const getQuarantineItemId = (item) => {
        return item.id
            || item.quarantineItemId
            || item.quarantine_item_id
            || item.itemId
            || item.item_id
            || null;
    };

    const getStoredQuarantineItems = (notification) => {
        return quarantineItemsByNotificationId[notification.id] || [];
    };

    const handleQuarantineFile = async (notification, eventResponse) => {
        const endpointId = getEndpointIdForQuarantine(notification, eventResponse);
        const filePath = getFilePathForQuarantine(eventResponse);

        if (!endpointId || !filePath) {
            const message = 'Geen endpoint ID of bestandspad gevonden voor deze melding.';
            setActionError(message);
            showCommandToast({
                variant: 'danger',
                title: 'Opdracht mislukt',
                message,
            });
            return;
        }

        setActionError('');
        setActionLoadingKey(`${notification.id}:quarantine_file`);

        try {
            const response = await axios.post(
                `/api/v1/notifications/${notification.id}/quarantine-file`,
                {
                    endpoint_id: endpointId,
                    file_path: filePath,
                },
                {
                    headers,
                }
            );

            if (response.data?.action) {
                updateNotificationAction(notification.id, response.data.action);
            }

            showCommandToast({
                variant: 'success',
                title: 'Opdracht verzonden',
                message: response.data?.message || 'Quarantaine taak is aangemaakt.',
            });
        } catch (error) {
            console.error(error);

            const message = error.response?.data?.message
                || 'Quarantaine taak kon niet worden aangemaakt.';

            setActionError(message);

            if (error.response?.data?.action) {
                updateNotificationAction(notification.id, error.response.data.action);
            }

            showCommandToast({
                variant: 'danger',
                title: 'Opdracht mislukt',
                message,
            });
        } finally {
            setActionLoadingKey(null);
        }
    };

    const handleUninstallEndpoint = async (notification, eventResponse) => {
        const endpointId = getEndpointIdForAction(notification, eventResponse);

        if (!endpointId) {
            const message = 'Geen endpoint ID gevonden voor deze melding.';
            setActionError(message);
            showCommandToast({
                variant: 'danger',
                title: 'Opdracht mislukt',
                message,
            });
            return;
        }

        const confirmed = window.confirm(
            'Weet je zeker dat je een uninstall taak voor deze endpoint wilt aanmaken?'
        );

        if (!confirmed) {
            return;
        }

        setActionError('');
        setActionLoadingKey(`${notification.id}:uninstall_endpoint`);

        try {
            const response = await axios.post(
                `/api/v1/notifications/${notification.id}/uninstall-endpoint`,
                {
                    endpoint_id: endpointId,
                },
                {
                    headers,
                }
            );

            if (response.data?.action) {
                updateNotificationAction(notification.id, response.data.action);
            }

            showCommandToast({
                variant: 'success',
                title: 'Opdracht verzonden',
                message: response.data?.message || 'Uninstall taak is aangemaakt.',
            });
        } catch (error) {
            console.error(error);

            const message = error.response?.data?.message
                || 'Uninstall taak kon niet worden aangemaakt.';

            setActionError(message);

            if (error.response?.data?.action) {
                updateNotificationAction(notification.id, error.response.data.action);
            }

            showCommandToast({
                variant: 'danger',
                title: 'Opdracht mislukt',
                message,
            });
        } finally {
            setActionLoadingKey(null);
        }
    };

    const handleGetQuarantineItemsList = async (notification, eventResponse) => {
        const endpointId = getEndpointIdForAction(notification, eventResponse);
        const filePath = getFilePathForAction(eventResponse);
        const companyId = eventResponse.companyId || notification.bitdefender_id || null;

        setActionError('');
        setActionLoadingKey(`${notification.id}:list_quarantine_items`);

        try {
            const response = await axios.get(
                `/api/v1/notifications/${notification.id}/quarantine-items`,
                {
                    headers,
                    params: {
                        company_id: companyId,
                        endpoint_id: endpointId,
                        file_path: filePath,
                    },
                }
            );

            if (response.data?.action) {
                updateNotificationAction(notification.id, response.data.action);
            }

            const items = extractQuarantineItemsFromResponse(response.data);

            setQuarantineItemsByNotificationId((current) => ({
                ...current,
                [notification.id]: items,
            }));

            showCommandToast({
                variant: 'success',
                title: 'Quarantaine opgehaald',
                message: `${items.length} quarantaine item(s) gevonden.`,
            });
        } catch (error) {
            console.error(error);

            const message = error.response?.data?.message
                || 'Quarantaine items konden niet worden opgehaald.';

            setActionError(message);

            if (error.response?.data?.action) {
                updateNotificationAction(notification.id, error.response.data.action);
            }

            showCommandToast({
                variant: 'danger',
                title: 'Opdracht mislukt',
                message,
            });
        } finally {
            setActionLoadingKey(null);
        }
    };

    const handleRemoveQuarantineItems = async (notification, eventResponse) => {
        const items = getStoredQuarantineItems(notification);
        const quarantineItemIds = items
            .map((item) => getQuarantineItemId(item))
            .filter(Boolean);

        if (quarantineItemIds.length === 0) {
            const message = 'Er zijn geen quarantaine item IDs gevonden. Haal eerst de quarantaine items op.';
            setActionError(message);
            showCommandToast({
                variant: 'danger',
                title: 'Geen items gevonden',
                message,
            });
            return;
        }

        const confirmed = window.confirm(
            `Weet je zeker dat je ${quarantineItemIds.length} quarantaine item(s) wilt verwijderen?`
        );

        if (!confirmed) {
            return;
        }

        setActionError('');
        setActionLoadingKey(`${notification.id}:remove_quarantine_items`);

        try {
            const response = await axios.post(
                `/api/v1/notifications/${notification.id}/remove-quarantine-items`,
                {
                    quarantine_item_ids: quarantineItemIds,
                    endpoint_id: getEndpointIdForAction(notification, eventResponse),
                    file_path: getFilePathForAction(eventResponse),
                },
                {
                    headers,
                }
            );

            if (response.data?.action) {
                updateNotificationAction(notification.id, response.data.action);
            }

            showCommandToast({
                variant: 'success',
                title: 'Opdracht verzonden',
                message: response.data?.message || 'Verwijdertaak voor quarantaine item(s) is aangemaakt.',
            });
        } catch (error) {
            console.error(error);

            const message = error.response?.data?.message
                || 'Verwijdertaak voor quarantaine item(s) kon niet worden aangemaakt.';

            setActionError(message);

            if (error.response?.data?.action) {
                updateNotificationAction(notification.id, error.response.data.action);
            }

            showCommandToast({
                variant: 'danger',
                title: 'Opdracht mislukt',
                message,
            });
        } finally {
            setActionLoadingKey(null);
        }
    };

    const getSeverityBadge = (severity, score) => {
        const normalized = String(severity || '').toLowerCase();
        const numericScore = Number(score || 0);

        if (normalized === 'critical' || numericScore >= 80) {
            return 'danger';
        }

        if (normalized === 'high' || numericScore >= 60) {
            return 'danger';
        }

        if (normalized === 'medium' || numericScore >= 40) {
            return 'warning';
        }

        if (normalized === 'low' || numericScore > 0) {
            return 'info';
        }

        return 'secondary';
    };

    const getActionBadge = (action) => {
        const normalized = String(action || '').toLowerCase();

        if (normalized.includes('block')) {
            return 'success';
        }

        if (normalized.includes('allow')) {
            return 'warning';
        }

        if (normalized.includes('delete') || normalized.includes('quarantine')) {
            return 'danger';
        }

        return 'secondary';
    };

    const getAvcStatusBadge = (status) => {
        const normalized = String(status || '').toLowerCase();

        if (normalized.includes('disinfected') || normalized.includes('blocked')) {
            return 'success';
        }

        if (normalized.includes('failed')) {
            return 'danger';
        }

        if (normalized.includes('pending')) {
            return 'warning';
        }

        return 'secondary';
    };

    const getAvStatusBadge = (eventResponse) => {
        if (
            Number(eventResponse.blocked) === 1 ||
            Number(eventResponse.quarantined) === 1 ||
            Number(eventResponse.deleted) === 1
        ) {
            return 'success';
        }

        if (
            Number(eventResponse.present) === 1 ||
            String(eventResponse.final_status || '').toLowerCase().includes('present')
        ) {
            return 'danger';
        }

        if (Number(eventResponse.ignored) === 1) {
            return 'warning';
        }

        return 'secondary';
    };

    const getStatusBadge = (status) => {
        if (status === 'closed') {
            return 'success';
        }

        if (status === 'active') {
            return 'warning';
        }

        if (status === 'paused') {
            return 'secondary';
        }

        return 'info';
    };

    const getNodeTypeLabel = (type) => {
        switch (type) {
            case 'process_execution':
                return 'Proces';
            case 'file':
                return 'Bestand';
            case 'registry':
                return 'Register';
            case 'domain':
                return 'Netwerk';
            default:
                return type || 'Onbekend';
        }
    };

    const getNodeTypeBadge = (type) => {
        switch (type) {
            case 'process_execution':
                return 'primary';
            case 'file':
                return 'secondary';
            case 'registry':
                return 'warning';
            case 'domain':
                return 'info';
            default:
                return 'light';
        }
    };

    const formatEventDate = (value) => {
        if (!value) {
            return '-';
        }

        const date = new Date(value);

        if (Number.isNaN(date.getTime())) {
            return String(value);
        }

        return date.toLocaleString();
    };

    const renderCopyableCode = (value) => {
        if (value === null || value === undefined || value === '') {
            return null;
        }

        return (
            <code
                className="d-block text-break bg-body-tertiary border rounded px-2 py-1 mt-1"
                style={{
                    fontSize: '0.78rem',
                }}
            >
                {String(value)}
            </code>
        );
    };

    const renderInfoItem = (label, value, { code = false } = {}) => {
        if (value === null || value === undefined || value === '') {
            return null;
        }

        return (
            <div className="mb-2">
                <div className="text-muted small">{label}</div>
                {code ? (
                    renderCopyableCode(value)
                ) : (
                    <div className="fw-semibold text-break">{String(value)}</div>
                )}
            </div>
        );
    };

    const renderRawJsonDetails = (notification, eventResponse, open = false) => {
        const isCopied = copiedJsonId === notification.id;

        return (
            <div className="mt-3">
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
                    <div className="text-muted small">Ruwe JSON</div>

                    <Button
                        type="button"
                        size="sm"
                        variant={isCopied ? 'success' : 'outline-secondary'}
                        className="d-inline-flex align-items-center justify-content-center"
                        style={{ width: 34, height: 31 }}
                        onClick={() => copyJsonToClipboard(notification, eventResponse)}
                        title={isCopied ? 'JSON gekopieerd' : 'Kopieer JSON'}
                        aria-label={isCopied ? 'JSON gekopieerd' : 'Kopieer JSON'}
                    >
                        <ClipboardIcon checked={isCopied} />
                    </Button>
                </div>

                <details open={open}>
                    <summary className="text-muted small" style={{ cursor: 'pointer' }}>
                        Toon JSON
                    </summary>

                    <pre
                        className="m-0 mt-2 font-monospace bg-body border rounded p-3"
                        style={{
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            maxHeight: 320,
                            overflow: 'auto',
                            maxWidth: '100%',
                            fontSize: '0.78rem',
                        }}
                    >
                        {JSON.stringify(eventResponse ?? {}, null, 2)}
                    </pre>
                </details>
            </div>
        );
    };

    const renderPanelHeader = ({
                                   title,
                                   subtitle,
                                   badges = [],
                                   notification,
                                   eventResponse,
                               }) => {
        const isCopied = copiedJsonId === notification.id;

        const latestQuarantineAction = getLatestAction(notification, 'quarantine_file');
        const latestUninstallAction = getLatestAction(notification, 'uninstall_endpoint');
        const latestListQuarantineAction = getLatestAction(notification, 'list_quarantine_items');
        const latestRemoveQuarantineAction = getLatestAction(notification, 'remove_quarantine_items');

        const quarantineAvailable = canQuarantineFile(notification, eventResponse);
        const endpointId = getEndpointIdForAction(notification, eventResponse);
        const filePath = getFilePathForAction(eventResponse);
        const quarantineItems = getStoredQuarantineItems(notification);

        const quarantineLoading = actionLoadingKey === `${notification.id}:quarantine_file`;
        const uninstallLoading = actionLoadingKey === `${notification.id}:uninstall_endpoint`;
        const listQuarantineLoading = actionLoadingKey === `${notification.id}:list_quarantine_items`;
        const removeQuarantineLoading = actionLoadingKey === `${notification.id}:remove_quarantine_items`;

        const quarantineSuccessful = latestQuarantineAction?.status === 'success';
        const quarantinePending = latestQuarantineAction?.status === 'pending';

        const uninstallSuccessful = latestUninstallAction?.status === 'success';
        const uninstallPending = latestUninstallAction?.status === 'pending';

        const removeQuarantineSuccessful = latestRemoveQuarantineAction?.status === 'success';
        const removeQuarantinePending = latestRemoveQuarantineAction?.status === 'pending';

        const canListQuarantineItems = Boolean(endpointId || filePath || eventResponse.companyId || notification.bitdefender_id);
        const canRemoveQuarantineItems = quarantineItems.length > 0;

        const actionBadges = [
            ...badges,
            latestQuarantineAction
                ? {
                    label: latestQuarantineAction.status === 'success'
                        ? 'Quarantaine taak aangemaakt'
                        : latestQuarantineAction.status === 'failed'
                            ? 'Quarantaine mislukt'
                            : 'Quarantaine in behandeling',
                    bg: latestQuarantineAction.status === 'success'
                        ? 'success'
                        : latestQuarantineAction.status === 'failed'
                            ? 'danger'
                            : 'warning',
                }
                : null,
            latestUninstallAction
                ? {
                    label: latestUninstallAction.status === 'success'
                        ? 'Uninstall taak aangemaakt'
                        : latestUninstallAction.status === 'failed'
                            ? 'Uninstall mislukt'
                            : 'Uninstall in behandeling',
                    bg: latestUninstallAction.status === 'success'
                        ? 'success'
                        : latestUninstallAction.status === 'failed'
                            ? 'danger'
                            : 'warning',
                }
                : null,
            latestListQuarantineAction
                ? {
                    label: latestListQuarantineAction.status === 'success'
                        ? 'Quarantaine items opgehaald'
                        : latestListQuarantineAction.status === 'failed'
                            ? 'Ophalen quarantaine mislukt'
                            : 'Quarantaine ophalen bezig',
                    bg: latestListQuarantineAction.status === 'success'
                        ? 'info'
                        : latestListQuarantineAction.status === 'failed'
                            ? 'danger'
                            : 'warning',
                }
                : null,
            latestRemoveQuarantineAction
                ? {
                    label: latestRemoveQuarantineAction.status === 'success'
                        ? 'Verwijdertaak aangemaakt'
                        : latestRemoveQuarantineAction.status === 'failed'
                            ? 'Verwijderen mislukt'
                            : 'Verwijderen in behandeling',
                    bg: latestRemoveQuarantineAction.status === 'success'
                        ? 'success'
                        : latestRemoveQuarantineAction.status === 'failed'
                            ? 'danger'
                            : 'warning',
                }
                : null,
        ].filter(Boolean);

        return (
            <div className="d-flex flex-wrap align-items-start justify-content-between gap-3 mb-3">
                <div>
                    <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                        <h6 className="mb-0">{title}</h6>

                        {actionBadges.map((badge, index) => (
                            <Badge
                                key={`${badge.label}-${index}`}
                                bg={badge.bg || 'secondary'}
                                text={badge.text}
                                className={badge.className}
                            >
                                {badge.label}
                            </Badge>
                        ))}
                    </div>

                    {subtitle && (
                        <div className="text-muted small">
                            {subtitle}
                        </div>
                    )}
                </div>

                <div className="d-flex flex-wrap align-items-center gap-2">
                    {quarantineAvailable && (
                        <Button
                            type="button"
                            size="sm"
                            variant={quarantineSuccessful ? 'success' : 'outline-danger'}
                            className="d-inline-flex align-items-center gap-2"
                            disabled={quarantineLoading || quarantineSuccessful || quarantinePending}
                            onClick={() => handleQuarantineFile(notification, eventResponse)}
                            title={quarantineSuccessful ? 'Quarantaine taak is al aangemaakt' : 'Bestand in quarantaine plaatsen'}
                        >
                            {quarantineLoading ? <Spinner animation="border" size="sm" /> : <ShieldIcon />}

                            <span className="d-none d-md-inline">
                                {quarantineSuccessful
                                    ? 'Quarantaine aangemaakt'
                                    : quarantinePending
                                        ? 'In behandeling'
                                        : 'Quarantaine'}
                            </span>
                        </Button>
                    )}

                    {canListQuarantineItems && (
                        <Button
                            type="button"
                            size="sm"
                            variant="outline-primary"
                            className="d-inline-flex align-items-center gap-2"
                            disabled={listQuarantineLoading}
                            onClick={() => handleGetQuarantineItemsList(notification, eventResponse)}
                            title="Quarantaine items ophalen"
                        >
                            {listQuarantineLoading ? <Spinner animation="border" size="sm" /> : <ListIcon />}

                            <span className="d-none d-md-inline">
                                Quarantaine items
                            </span>
                        </Button>
                    )}

                    {canRemoveQuarantineItems && (
                        <Button
                            type="button"
                            size="sm"
                            variant={removeQuarantineSuccessful ? 'success' : 'outline-danger'}
                            className="d-inline-flex align-items-center gap-2"
                            disabled={removeQuarantineLoading || removeQuarantineSuccessful || removeQuarantinePending}
                            onClick={() => handleRemoveQuarantineItems(notification, eventResponse)}
                            title="Quarantaine items verwijderen"
                        >
                            {removeQuarantineLoading ? <Spinner animation="border" size="sm" /> : <TrashIcon />}

                            <span className="d-none d-md-inline">
                                Verwijder items ({quarantineItems.length})
                            </span>
                        </Button>
                    )}

                    {endpointId && (
                        <Button
                            type="button"
                            size="sm"
                            variant={uninstallSuccessful ? 'success' : 'outline-warning'}
                            className="d-inline-flex align-items-center gap-2"
                            disabled={uninstallLoading || uninstallSuccessful || uninstallPending}
                            onClick={() => handleUninstallEndpoint(notification, eventResponse)}
                            title={uninstallSuccessful ? 'Uninstall taak is al aangemaakt' : 'Endpoint uninstall taak aanmaken'}
                        >
                            {uninstallLoading ? <Spinner animation="border" size="sm" /> : <PowerIcon />}

                            <span className="d-none d-md-inline">
                                {uninstallSuccessful
                                    ? 'Uninstall aangemaakt'
                                    : uninstallPending
                                        ? 'In behandeling'
                                        : 'Uninstall'}
                            </span>
                        </Button>
                    )}

                    <Button
                        type="button"
                        size="sm"
                        variant={isCopied ? 'success' : 'outline-secondary'}
                        className="d-inline-flex align-items-center justify-content-center"
                        style={{ width: 34, height: 31 }}
                        onClick={() => copyJsonToClipboard(notification, eventResponse)}
                        title={isCopied ? 'JSON gekopieerd' : 'Kopieer JSON'}
                        aria-label={isCopied ? 'JSON gekopieerd' : 'Kopieer JSON'}
                    >
                        <ClipboardIcon checked={isCopied} />
                    </Button>
                </div>
            </div>
        );
    };

    const renderEndpointCard = (notification, eventResponse) => {
        return (
            <div className="border rounded bg-body p-3 h-100">
                <div className="fw-semibold mb-2">Endpoint</div>

                {renderInfoItem('Computer', eventResponse.computer_name || notification.computer_name)}
                {renderInfoItem('FQDN', eventResponse.computer_fqdn)}
                {renderInfoItem('IP-adres', eventResponse.computer_ip || notification.computer_ip)}
                {renderInfoItem('Computer ID', eventResponse.computer_id || notification.computer_id, { code: true })}
                {renderInfoItem('Company ID', eventResponse.companyId, { code: true })}

                {eventResponse.is_container_host !== undefined && (
                    <div className="mb-2">
                        <div className="text-muted small">Container host</div>
                        <Badge bg={getBooleanBadge(eventResponse.is_container_host)}>
                            {translateBoolean(eventResponse.is_container_host)}
                        </Badge>
                    </div>
                )}
            </div>
        );
    };

    const getNodePrimaryPath = (node) => {
        const details = node?.details ?? {};

        return details?.file?.path
            || details?.name
            || details?.registry?.key
            || details?.domain?.requested_url
            || node?.name
            || '-';
    };

    const getNodeProcessName = (node) => {
        return node?.details?.process?.name
            || node?.details?.process?.path
            || null;
    };

    const getNodeUser = (node) => {
        return node?.details?.process?.user?.user_name
            || null;
    };

    const getNodeExecutionDate = (node) => {
        return node?.details?.process?.execution_date
            || null;
    };

    const getNodeHashes = (node) => {
        const details = node?.details ?? {};

        return {
            md5: details?.file?.md5 || details?.md5 || null,
            sha256: details?.file?.sha256 || details?.sha256 || null,
        };
    };

    const getNodeExtraDetails = (node) => {
        const details = node?.details ?? {};

        if (node?.type === 'process_execution') {
            return [
                ['PID', details?.process?.pid],
                ['Proces', details?.process?.name],
                ['Parent', details?.process?.parent?.name],
                ['Gebruiker', details?.process?.user?.user_name],
                ['Gestart', formatEventDate(details?.process?.execution_date)],
                ['Command line', details?.process?.command_line, true],
            ];
        }

        if (node?.type === 'registry') {
            return [
                ['Register key', details?.registry?.key, true],
                ['Waarde', details?.registry?.value],
                ['Data', details?.registry?.data],
                ['Proces', details?.process?.name],
                ['Proces pad', details?.process?.path, true],
            ];
        }

        if (node?.type === 'domain') {
            return [
                ['URL / IP', details?.domain?.requested_url],
                ['Remote poort', details?.domain?.remote_port],
                ['Applicatie', details?.domain?.source_application, true],
                ['Proces', details?.process?.name],
                ['Proces pad', details?.process?.path, true],
            ];
        }

        if (node?.type === 'file') {
            return [
                ['Bestandspad', details?.name, true],
            ];
        }

        return [];
    };

    const groupNodes = (nodes = []) => {
        const alertNodes = nodes.filter((node) => Number(node.total_alerts || 0) > 0);
        const processNodes = nodes.filter((node) => node.type === 'process_execution' && Number(node.total_alerts || 0) === 0);
        const fileNodes = nodes.filter((node) => node.type === 'file' && Number(node.total_alerts || 0) === 0);
        const registryNodes = nodes.filter((node) => node.type === 'registry' && Number(node.total_alerts || 0) === 0);
        const domainNodes = nodes.filter((node) => node.type === 'domain' && Number(node.total_alerts || 0) === 0);
        const otherNodes = nodes.filter((node) => !['process_execution', 'file', 'registry', 'domain'].includes(node.type));

        return [
            {
                key: 'alerts',
                title: 'Belangrijke nodes met alerts',
                nodes: alertNodes,
                defaultOpen: true,
            },
            {
                key: 'processes',
                title: 'Processen',
                nodes: processNodes,
                defaultOpen: false,
            },
            {
                key: 'files',
                title: 'Bestanden',
                nodes: fileNodes,
                defaultOpen: false,
            },
            {
                key: 'registry',
                title: 'Registerwijzigingen',
                nodes: registryNodes,
                defaultOpen: false,
            },
            {
                key: 'domains',
                title: 'Netwerk / domeinen',
                nodes: domainNodes,
                defaultOpen: false,
            },
            {
                key: 'other',
                title: 'Overige nodes',
                nodes: otherNodes,
                defaultOpen: false,
            },
        ].filter((group) => group.nodes.length > 0);
    };

    const renderNodeCard = (node, index) => {
        const hashes = getNodeHashes(node);
        const alertCount = Number(node.total_alerts || 0);
        const extraDetails = getNodeExtraDetails(node);

        return (
            <div
                key={`${node.id ?? node.name}-${index}`}
                className={`border rounded bg-body p-3 ${alertCount > 0 ? 'border-warning' : ''}`}
            >
                <div className="d-flex flex-wrap align-items-start justify-content-between gap-2">
                    <div className="min-w-0">
                        <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                            <Badge bg={getNodeTypeBadge(node.type)}>
                                {getNodeTypeLabel(node.type)}
                            </Badge>

                            {alertCount > 0 && (
                                <Badge bg="danger">
                                    {alertCount} alert{alertCount === 1 ? '' : 's'}
                                </Badge>
                            )}
                        </div>

                        <div className="fw-semibold text-break">
                            {node.name || 'Onbekende node'}
                        </div>

                        <div className="text-muted small text-break">
                            {getNodePrimaryPath(node)}
                        </div>
                    </div>

                    {node.id && (
                        <Badge bg="light" text="dark" className="border">
                            ID: {node.id}
                        </Badge>
                    )}
                </div>

                <div className="row g-3 mt-1">
                    {getNodeProcessName(node) && (
                        <div className="col-md-4">
                            {renderInfoItem('Proces', getNodeProcessName(node))}
                        </div>
                    )}

                    {getNodeUser(node) && (
                        <div className="col-md-4">
                            {renderInfoItem('Gebruiker', getNodeUser(node))}
                        </div>
                    )}

                    {getNodeExecutionDate(node) && (
                        <div className="col-md-4">
                            {renderInfoItem('Uitgevoerd', formatEventDate(getNodeExecutionDate(node)))}
                        </div>
                    )}
                </div>

                {(hashes.md5 || hashes.sha256 || extraDetails.length > 0) && (
                    <details className="mt-2">
                        <summary className="text-muted small" style={{ cursor: 'pointer' }}>
                            Toon technische details
                        </summary>

                        <div className="mt-2">
                            {hashes.md5 && renderInfoItem('MD5', hashes.md5, { code: true })}
                            {hashes.sha256 && renderInfoItem('SHA256', hashes.sha256, { code: true })}

                            {extraDetails.map(([label, value, code]) => (
                                <Fragment key={`${node.id}-${label}`}>
                                    {renderInfoItem(label, value, { code })}
                                </Fragment>
                            ))}
                        </div>
                    </details>
                )}
            </div>
        );
    };

    const renderAvcDetailsPanel = (notification, eventResponse) => {
        return (
            <div className="p-3 bg-body-tertiary border-top">
                {renderPanelHeader({
                    title: translateModule('avc'),
                    subtitle: `${notification.company_name || 'Onbekend bedrijf'} · ${eventResponse.computer_name || notification.computer_name || 'Onbekende computer'}`,
                    notification,
                    eventResponse,
                    badges: [
                        eventResponse.status ? { label: translateStatus(eventResponse.status), bg: getAvcStatusBadge(eventResponse.status) } : null,
                        eventResponse.exploit_type ? { label: eventResponse.exploit_type, bg: 'secondary' } : null,
                        eventResponse.count !== undefined ? { label: `Aantal: ${eventResponse.count}`, bg: 'light', text: 'dark', className: 'border' } : null,
                        eventResponse.product_installed ? { label: eventResponse.product_installed, bg: 'primary' } : null,
                    ].filter(Boolean),
                })}

                <div className="row g-3 mb-3">
                    <div className="col-lg-4">
                        <div className="border rounded bg-body p-3 h-100">
                            <div className="fw-semibold mb-2">Detectie</div>

                            {renderInfoItem('Type', eventResponse.exploit_type || '-')}
                            {renderInfoItem('Status', translateStatus(eventResponse.status))}
                            {renderInfoItem('Laatste blokkade', formatEventDate(eventResponse.last_blocked))}
                            {renderInfoItem('Aantal', eventResponse.count)}
                        </div>
                    </div>

                    <div className="col-lg-4">
                        {renderEndpointCard(notification, eventResponse)}
                    </div>

                    <div className="col-lg-4">
                        <div className="border rounded bg-body p-3 h-100">
                            <div className="fw-semibold mb-2">Gebruiker</div>

                            {renderInfoItem('Naam', eventResponse.user?.name)}
                            {renderInfoItem('SID', eventResponse.user?.id, { code: true })}
                        </div>
                    </div>
                </div>

                <div className="border rounded bg-body p-3 mb-3">
                    <div className="fw-semibold mb-2">Procesinformatie</div>

                    <div className="row g-3">
                        <div className="col-lg-6">
                            {renderInfoItem('Verdacht proces', eventResponse.exploit_path, { code: true })}
                            {renderInfoItem('Command line', eventResponse.process_command_line, { code: true })}
                        </div>

                        <div className="col-lg-6">
                            {renderInfoItem('Parent proces ID', eventResponse.parent_process_id)}
                            {renderInfoItem('Parent proces', eventResponse.parent_process_path, { code: true })}
                        </div>
                    </div>
                </div>

                {renderRawJsonDetails(notification, eventResponse)}
            </div>
        );
    };

    const renderAvDetailsPanel = (notification, eventResponse) => {
        return (
            <div className="p-3 bg-body-tertiary border-top">
                {renderPanelHeader({
                    title: translateModule('av'),
                    subtitle: `${notification.company_name || 'Onbekend bedrijf'} · ${eventResponse.computer_name || notification.computer_name || 'Onbekende computer'}`,
                    notification,
                    eventResponse,
                    badges: [
                        eventResponse.final_status ? { label: translateStatus(eventResponse.final_status), bg: getAvStatusBadge(eventResponse) } : null,
                        eventResponse.malware_type ? { label: eventResponse.malware_type, bg: 'secondary' } : null,
                        eventResponse.product_installed ? { label: eventResponse.product_installed, bg: 'primary' } : null,
                    ].filter(Boolean),
                })}

                <div className="row g-3 mb-3">
                    <div className="col-lg-4">
                        <div className="border rounded bg-body p-3 h-100">
                            <div className="fw-semibold mb-2">Malware</div>

                            {renderInfoItem('Naam', eventResponse.malware_name || '-')}
                            {renderInfoItem('Type', eventResponse.malware_type)}
                            {renderInfoItem('Status', translateStatus(eventResponse.final_status))}
                            {renderInfoItem('Tijdstip', formatEventDate(eventResponse.timestamp))}
                        </div>
                    </div>

                    <div className="col-lg-4">
                        {renderEndpointCard(notification, eventResponse)}
                    </div>

                    <div className="col-lg-4">
                        <div className="border rounded bg-body p-3 h-100">
                            <div className="fw-semibold mb-2">Gebruiker</div>

                            {renderInfoItem('Naam', eventResponse.user?.name)}
                            {renderInfoItem('SID', eventResponse.user?.id, { code: true })}
                        </div>
                    </div>
                </div>

                <div className="border rounded bg-body p-3 mb-3">
                    <div className="fw-semibold mb-2">Bestand & indicator</div>

                    {renderInfoItem('Bestandspad', eventResponse.file_path, { code: true })}
                    {renderInfoItem('Hash', eventResponse.hash, { code: true })}
                    {renderInfoItem('Signatures', eventResponse.signaturesNumber)}
                    {renderInfoItem('Scan engine type', eventResponse.scanEngineType)}
                </div>

                <div className="border rounded bg-body p-3 mb-3">
                    <div className="fw-semibold mb-2">Acties</div>

                    <div className="d-flex flex-wrap gap-2">
                        {[
                            ['Geblokkeerd', eventResponse.blocked],
                            ['Opgeschoond', eventResponse.cleaned],
                            ['Verwijderd', eventResponse.deleted],
                            ['Quarantaine', eventResponse.quarantined],
                            ['Genegeerd', eventResponse.ignored],
                            ['Nog aanwezig', eventResponse.present],
                        ].map(([label, value]) => (
                            <Badge key={label} bg={getBooleanBadge(value)}>
                                {label}: {translateBoolean(value)}
                            </Badge>
                        ))}
                    </div>
                </div>

                {renderRawJsonDetails(notification, eventResponse)}
            </div>
        );
    };

    const renderRegistrationDetailsPanel = (notification, eventResponse) => {
        return (
            <div className="p-3 bg-body-tertiary border-top">
                {renderPanelHeader({
                    title: translateModule('registration'),
                    subtitle: `${notification.company_name || 'Onbekend bedrijf'} · ${eventResponse.computer_name || notification.computer_name || 'Onbekende computer'}`,
                    notification,
                    eventResponse,
                    badges: [
                        eventResponse.product_registration ? { label: translateStatus(eventResponse.product_registration), bg: eventResponse.product_registration === 'registered' ? 'success' : 'warning' } : null,
                        eventResponse.product_installed ? { label: eventResponse.product_installed, bg: 'primary' } : null,
                    ].filter(Boolean),
                })}

                <div className="row g-3 mb-3">
                    <div className="col-lg-6">
                        <div className="border rounded bg-body p-3 h-100">
                            <div className="fw-semibold mb-2">Registratie</div>

                            {renderInfoItem('Nieuwe status', translateStatus(eventResponse.product_registration || '-'))}
                            {renderInfoItem('Oude register status', eventResponse.oldData?.registerStatus)}
                        </div>
                    </div>

                    <div className="col-lg-6">
                        {renderEndpointCard(notification, eventResponse)}
                    </div>
                </div>

                {renderRawJsonDetails(notification, eventResponse)}
            </div>
        );
    };

    const renderHwidChangeDetailsPanel = (notification, eventResponse) => {
        return (
            <div className="p-3 bg-body-tertiary border-top">
                {renderPanelHeader({
                    title: translateModule('hwid-change'),
                    subtitle: `${notification.company_name || 'Onbekend bedrijf'} · ${eventResponse.computer_name || notification.computer_name || 'Onbekende computer'}`,
                    notification,
                    eventResponse,
                    badges: [
                        eventResponse.product_installed ? { label: eventResponse.product_installed, bg: 'primary' } : null,
                    ].filter(Boolean),
                })}

                <div className="row g-3 mb-3">
                    <div className="col-lg-6">
                        <div className="border rounded bg-body p-3 h-100">
                            <div className="fw-semibold mb-2">Wijziging</div>

                            {renderInfoItem('Oude HWID', eventResponse.old_hwid, { code: true })}
                            {renderInfoItem('Nieuwe HWID', eventResponse.new_hwid, { code: true })}
                            {renderInfoItem('Endpoint ID', eventResponse.endpointId, { code: true })}
                        </div>
                    </div>

                    <div className="col-lg-6">
                        {renderEndpointCard(notification, eventResponse)}
                    </div>
                </div>

                {renderRawJsonDetails(notification, eventResponse)}
            </div>
        );
    };

    const renderModulesDetailsPanel = (notification, eventResponse) => {
        const features = Array.isArray(eventResponse.oldData?.features)
            ? eventResponse.oldData.features
            : [];

        const enabledCount = features.filter((feature) => feature.enabled).length;
        const disabledCount = features.filter((feature) => !feature.enabled).length;
        const expiredCount = features.filter((feature) => feature.registrationStatus === 'expired').length;
        const notFunctioningCount = features.filter((feature) => feature.isFunctioning === false).length;

        return (
            <div className="p-3 bg-body-tertiary border-top">
                {renderPanelHeader({
                    title: translateModule('modules'),
                    subtitle: `${notification.company_name || 'Onbekend bedrijf'} · ${eventResponse.computer_name || notification.computer_name || 'Onbekende computer'}`,
                    notification,
                    eventResponse,
                    badges: [
                        eventResponse.product_installed ? { label: eventResponse.product_installed, bg: 'primary' } : null,
                        { label: `${features.length} features`, bg: 'secondary' },
                        { label: `${enabledCount} actief`, bg: 'success' },
                        disabledCount > 0 ? { label: `${disabledCount} uit`, bg: 'secondary' } : null,
                        expiredCount > 0 ? { label: `${expiredCount} verlopen`, bg: 'warning' } : null,
                        notFunctioningCount > 0 ? { label: `${notFunctioningCount} functioneert niet`, bg: 'danger' } : null,
                    ].filter(Boolean),
                })}

                <div className="row g-3 mb-3">
                    <div className="col-lg-4">
                        {renderEndpointCard(notification, eventResponse)}
                    </div>

                    <div className="col-lg-8">
                        <div className="border rounded bg-body p-3 h-100">
                            <div className="fw-semibold mb-2">Module status</div>

                            <div className="d-flex flex-wrap gap-2">
                                {[
                                    ['Malware', eventResponse.malware_status],
                                    ['Antiphishing', eventResponse.aph_status],
                                    ['Firewall', eventResponse.firewall_status],
                                    ['AVC', eventResponse.avc_status],
                                    ['Web filtering', eventResponse.uc_web_filtering],
                                    ['Category filtering', eventResponse.uc_categ_filtering],
                                    ['Application control', eventResponse.uc_application_status],
                                    ['Device control', eventResponse.dp_status],
                                    ['Network monitor', eventResponse.network_monitor_status],
                                ].map(([label, value]) => (
                                    value !== undefined && (
                                        <Badge key={label} bg={getBooleanBadge(value)}>
                                            {label}: {translateBoolean(value)}
                                        </Badge>
                                    )
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {features.length > 0 && (
                    <div className="border rounded bg-body p-3 mb-3">
                        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
                            <div className="fw-semibold">Features</div>
                            <div className="text-muted small">{features.length} regels</div>
                        </div>

                        <div className="table-responsive">
                            <table className="table table-sm table-hover align-middle mb-0">
                                <thead>
                                <tr>
                                    <th>Feature</th>
                                    <th>Status</th>
                                    <th>Registratie</th>
                                    <th>Functioneert</th>
                                    <th>Extra</th>
                                </tr>
                                </thead>
                                <tbody>
                                {features.map((feature) => (
                                    <tr key={feature.id}>
                                        <td className="fw-semibold text-break">{feature.id}</td>
                                        <td>
                                            <Badge bg={feature.enabled ? 'success' : 'secondary'}>
                                                {feature.enabled ? 'Aan' : 'Uit'}
                                            </Badge>
                                        </td>
                                        <td>
                                            <Badge
                                                bg={feature.registrationStatus === 'expired' ? 'warning' : 'light'}
                                                text={feature.registrationStatus === 'expired' ? undefined : 'dark'}
                                                className={feature.registrationStatus === 'expired' ? '' : 'border'}
                                            >
                                                {feature.registrationStatus || '-'}
                                            </Badge>
                                        </td>
                                        <td>
                                            {feature.isFunctioning === undefined ? (
                                                <span className="text-muted">-</span>
                                            ) : (
                                                <Badge bg={feature.isFunctioning ? 'success' : 'danger'}>
                                                    {feature.isFunctioning ? 'Ja' : 'Nee'}
                                                </Badge>
                                            )}
                                        </td>
                                        <td className="small text-muted">
                                            {feature.scanType && <div>Scan type: {feature.scanType}</div>}
                                            {feature.operatingMode && <div>Mode: {feature.operatingMode}</div>}
                                            {feature.scanMode && (
                                                <div>
                                                    Scan mode: {feature.scanMode.current} / {feature.scanMode.requested}
                                                </div>
                                            )}
                                            {Array.isArray(feature.dependencies) && feature.dependencies.length > 0 && (
                                                <div>Afhankelijk van: {feature.dependencies.join(', ')}</div>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {renderRawJsonDetails(notification, eventResponse)}
            </div>
        );
    };

    const renderInstallDetailsPanel = (notification, eventResponse) => {
        return (
            <div className="p-3 bg-body-tertiary border-top">
                {renderPanelHeader({
                    title: translateModule('install'),
                    subtitle: `${notification.company_name || 'Onbekend bedrijf'} · ${eventResponse.computer_name || notification.computer_name || 'Onbekende computer'}`,
                    notification,
                    eventResponse,
                    badges: [
                        eventResponse.product_installed ? { label: eventResponse.product_installed, bg: 'primary' } : null,
                        { label: 'Endpoint geïnstalleerd', bg: 'success' },
                    ].filter(Boolean),
                })}

                <div className="row g-3 mb-3">
                    <div className="col-lg-6">
                        {renderEndpointCard(notification, eventResponse)}
                    </div>

                    <div className="col-lg-6">
                        <div className="border rounded bg-body p-3 h-100">
                            <div className="fw-semibold mb-2">Installatiegegevens</div>

                            {renderInfoItem('Endpoint ID', eventResponse.endpointId, { code: true })}
                            {renderInfoItem('Hardware ID', eventResponse.hwid, { code: true })}
                        </div>
                    </div>
                </div>

                {renderRawJsonDetails(notification, eventResponse)}
            </div>
        );
    };

    const renderUninstallDetailsPanel = (notification, eventResponse) => {
        return (
            <div className="p-3 bg-body-tertiary border-top">
                {renderPanelHeader({
                    title: translateModule('uninstall'),
                    subtitle: `${notification.company_name || 'Onbekend bedrijf'} · ${eventResponse.computer_name || notification.computer_name || 'Onbekende computer'}`,
                    notification,
                    eventResponse,
                    badges: [
                        eventResponse.product_installed ? { label: eventResponse.product_installed, bg: 'primary' } : null,
                        { label: 'Endpoint verwijderd', bg: 'warning' },
                    ].filter(Boolean),
                })}

                <div className="row g-3 mb-3">
                    <div className="col-lg-6">
                        {renderEndpointCard(notification, eventResponse)}
                    </div>

                    <div className="col-lg-6">
                        <div className="border rounded bg-body p-3 h-100">
                            <div className="fw-semibold mb-2">Verwijdergegevens</div>

                            {renderInfoItem('Endpoint ID', eventResponse.endpointId, { code: true })}
                            {renderInfoItem('Reden', translateUninstallReason(eventResponse.reason))}
                        </div>
                    </div>
                </div>

                {renderRawJsonDetails(notification, eventResponse)}
            </div>
        );
    };

    const renderEndpointMovedOutDetailsPanel = (notification, eventResponse) => {
        return (
            <div className="p-3 bg-body-tertiary border-top">
                {renderPanelHeader({
                    title: translateModule('endpoint-moved-out'),
                    subtitle: `${notification.company_name || 'Onbekend bedrijf'} · ${eventResponse.computer_name || notification.computer_name || 'Onbekende computer'}`,
                    notification,
                    eventResponse,
                    badges: [
                        eventResponse.product_installed ? { label: eventResponse.product_installed, bg: 'primary' } : null,
                        { label: 'Endpoint verplaatst', bg: 'warning' },
                    ].filter(Boolean),
                })}

                <div className="row g-3 mb-3">
                    <div className="col-lg-6">
                        {renderEndpointCard(notification, eventResponse)}
                    </div>

                    <div className="col-lg-6">
                        <div className="border rounded bg-body p-3 h-100">
                            <div className="fw-semibold mb-2">Verplaatsing</div>

                            {renderInfoItem('Endpoint ID', eventResponse.endpointId, { code: true })}
                            {renderInfoItem('Hardware ID', eventResponse.hwid, { code: true })}
                        </div>
                    </div>
                </div>

                {renderRawJsonDetails(notification, eventResponse)}
            </div>
        );
    };

    const renderTaskStatusDetailsPanel = (notification, eventResponse) => {
        const successful = Number(eventResponse.isSuccessful) === 1;
        const hasError = eventResponse.errorMessage || Number(eventResponse.errorCode) !== 0;

        return (
            <div className="p-3 bg-body-tertiary border-top">
                {renderPanelHeader({
                    title: translateModule('task-status'),
                    subtitle: `${notification.company_name || 'Onbekend bedrijf'} · ${eventResponse.targetName || eventResponse.computer_name || notification.computer_name || 'Onbekend doel'}`,
                    notification,
                    eventResponse,
                    badges: [
                        { label: successful ? 'Succesvol' : 'Niet succesvol', bg: successful ? 'success' : 'danger' },
                        { label: translateTaskStatus(eventResponse.status), bg: successful ? 'success' : 'warning' },
                        eventResponse.product_installed ? { label: eventResponse.product_installed, bg: 'primary' } : null,
                    ].filter(Boolean),
                })}

                <div className="row g-3 mb-3">
                    <div className="col-lg-6">
                        <div className="border rounded bg-body p-3 h-100">
                            <div className="fw-semibold mb-2">Taak</div>

                            {renderInfoItem('Taaknaam', eventResponse.taskName)}
                            {renderInfoItem('Taak ID', eventResponse.taskId, { code: true })}
                            {renderInfoItem('Taaktype', eventResponse.taskType)}
                            {renderInfoItem('Doel', eventResponse.targetName)}
                            {renderInfoItem('Gebruiker ID', eventResponse.userId, { code: true })}
                        </div>
                    </div>

                    <div className="col-lg-6">
                        <div className="border rounded bg-body p-3 h-100">
                            <div className="fw-semibold mb-2">Resultaat</div>

                            {renderInfoItem('Succesvol', translateBoolean(eventResponse.isSuccessful))}
                            {renderInfoItem('Status', translateTaskStatus(eventResponse.status))}
                            {renderInfoItem('Foutcode', eventResponse.errorCode)}

                            {hasError && renderInfoItem('Foutmelding', eventResponse.errorMessage || '-')}
                        </div>
                    </div>
                </div>

                <div className="row g-3 mb-3">
                    <div className="col-lg-12">
                        {renderEndpointCard(notification, eventResponse)}
                    </div>
                </div>

                {renderRawJsonDetails(notification, eventResponse)}
            </div>
        );
    };

    const renderAphDetailsPanel = (notification, eventResponse) => {
        return (
            <div className="p-3 bg-body-tertiary border-top">
                {renderPanelHeader({
                    title: translateModule('aph'),
                    subtitle: `${notification.company_name || 'Onbekend bedrijf'} · ${eventResponse.computer_name || notification.computer_name || 'Onbekende computer'}`,
                    notification,
                    eventResponse,
                    badges: [
                        eventResponse.status ? { label: translateStatus(eventResponse.status), bg: 'success' } : null,
                        eventResponse.aph_type ? { label: eventResponse.aph_type, bg: 'secondary' } : null,
                        eventResponse.count !== undefined ? { label: `Aantal: ${eventResponse.count}`, bg: 'light', text: 'dark', className: 'border' } : null,
                    ].filter(Boolean),
                })}

                <div className="row g-3 mb-3">
                    <div className="col-lg-4">
                        <div className="border rounded bg-body p-3 h-100">
                            <div className="fw-semibold mb-2">Blokkade</div>

                            {renderInfoItem('Type', eventResponse.aph_type)}
                            {renderInfoItem('Status', translateStatus(eventResponse.status))}
                            {renderInfoItem('Laatst geblokkeerd', formatEventDate(eventResponse.last_blocked))}
                            {renderInfoItem('Aantal', eventResponse.count)}
                        </div>
                    </div>

                    <div className="col-lg-4">
                        {renderEndpointCard(notification, eventResponse)}
                    </div>

                    <div className="col-lg-4">
                        <div className="border rounded bg-body p-3 h-100">
                            <div className="fw-semibold mb-2">Gebruiker</div>

                            {renderInfoItem('Naam', eventResponse.user?.name)}
                            {renderInfoItem('SID', eventResponse.user?.id, { code: true })}
                        </div>
                    </div>
                </div>

                <div className="border rounded bg-body p-3 mb-3">
                    <div className="fw-semibold mb-2">Geblokkeerde URL</div>
                    {renderInfoItem('URL', eventResponse.url, { code: true })}
                </div>

                {renderRawJsonDetails(notification, eventResponse)}
            </div>
        );
    };

    const renderUcDetailsPanel = (notification, eventResponse) => {
        return (
            <div className="p-3 bg-body-tertiary border-top">
                {renderPanelHeader({
                    title: translateModule('uc'),
                    subtitle: `${notification.company_name || 'Onbekend bedrijf'} · ${eventResponse.computer_name || notification.computer_name || 'Onbekende computer'}`,
                    notification,
                    eventResponse,
                    badges: [
                        eventResponse.status ? { label: translateStatus(eventResponse.status), bg: 'success' } : null,
                        eventResponse.uc_type ? { label: eventResponse.uc_type, bg: 'secondary' } : null,
                        eventResponse.block_type ? { label: translateStatus(eventResponse.block_type), bg: 'warning' } : null,
                        eventResponse.count !== undefined ? { label: `Aantal: ${eventResponse.count}`, bg: 'light', text: 'dark', className: 'border' } : null,
                    ].filter(Boolean),
                })}

                <div className="row g-3 mb-3">
                    <div className="col-lg-4">
                        <div className="border rounded bg-body p-3 h-100">
                            <div className="fw-semibold mb-2">Blokkade</div>

                            {renderInfoItem('Type', eventResponse.uc_type)}
                            {renderInfoItem('Blokkadetype', translateStatus(eventResponse.block_type))}
                            {renderInfoItem('Status', translateStatus(eventResponse.status))}
                            {renderInfoItem('Laatst geblokkeerd', formatEventDate(eventResponse.last_blocked))}
                            {renderInfoItem('Aantal', eventResponse.count)}
                        </div>
                    </div>

                    <div className="col-lg-4">
                        {renderEndpointCard(notification, eventResponse)}
                    </div>

                    <div className="col-lg-4">
                        <div className="border rounded bg-body p-3 h-100">
                            <div className="fw-semibold mb-2">Gebruiker</div>

                            {renderInfoItem('Naam', eventResponse.user?.name)}
                            {renderInfoItem('SID', eventResponse.user?.id, { code: true })}
                            {renderInfoItem(
                                'Categorieën',
                                Array.isArray(eventResponse.categories)
                                    ? eventResponse.categories.join(', ')
                                    : eventResponse.categories
                            )}
                        </div>
                    </div>
                </div>

                <div className="border rounded bg-body p-3 mb-3">
                    <div className="fw-semibold mb-2">Geblokkeerde URL</div>
                    {renderInfoItem('URL', eventResponse.url, { code: true })}
                </div>

                {renderRawJsonDetails(notification, eventResponse)}
            </div>
        );
    };

    const renderFwDetailsPanel = (notification, eventResponse) => {
        return (
            <div className="p-3 bg-body-tertiary border-top">
                {renderPanelHeader({
                    title: translateModule('fw'),
                    subtitle: `${notification.company_name || 'Onbekend bedrijf'} · ${eventResponse.computer_name || notification.computer_name || 'Onbekende computer'}`,
                    notification,
                    eventResponse,
                    badges: [
                        eventResponse.status ? { label: translateStatus(eventResponse.status), bg: 'success' } : null,
                        eventResponse.protocol_id ? { label: translateProtocol(eventResponse.protocol_id), bg: 'secondary' } : null,
                        eventResponse.count !== undefined ? { label: `Aantal: ${eventResponse.count}`, bg: 'light', text: 'dark', className: 'border' } : null,
                    ].filter(Boolean),
                })}

                <div className="row g-3 mb-3">
                    <div className="col-lg-6">
                        <div className="border rounded bg-body p-3 h-100">
                            <div className="fw-semibold mb-2">Firewall event</div>

                            {renderInfoItem('Status', translateStatus(eventResponse.status))}
                            {renderInfoItem('Protocol', translateProtocol(eventResponse.protocol_id))}
                            {renderInfoItem('Bron IP', eventResponse.source_ip)}
                            {renderInfoItem('Laatst geblokkeerd', formatEventDate(eventResponse.last_blocked))}
                            {renderInfoItem('Aantal', eventResponse.count)}
                        </div>
                    </div>

                    <div className="col-lg-6">
                        {renderEndpointCard(notification, eventResponse)}
                    </div>
                </div>

                {renderRawJsonDetails(notification, eventResponse)}
            </div>
        );
    };

    const renderNewIncidentDetailsPanel = (notification, eventResponse) => {
        const nodes = Array.isArray(eventResponse?.nodes) ? eventResponse.nodes : [];
        const nodeGroups = groupNodes(nodes);

        return (
            <div className="p-3 bg-body-tertiary border-top">
                {renderPanelHeader({
                    title: `Incident #${eventResponse.incident_number ?? notification.id}`,
                    subtitle: `${notification.company_name || 'Onbekend bedrijf'} · ${eventResponse.computer_name || notification.computer_name || 'Onbekende computer'}`,
                    notification,
                    eventResponse,
                    badges: [
                        eventResponse.severity ? { label: String(eventResponse.severity).toUpperCase(), bg: getSeverityBadge(eventResponse.severity, eventResponse.severity_score) } : null,
                        eventResponse.main_action ? { label: `Actie: ${eventResponse.main_action}`, bg: getActionBadge(eventResponse.main_action) } : null,
                        eventResponse.severity_score !== undefined ? { label: `Score: ${eventResponse.severity_score}`, bg: 'light', text: 'dark', className: 'border' } : null,
                        eventResponse.module ? { label: eventResponse.module, bg: 'secondary' } : null,
                        nodes.length > 0 ? { label: `${nodes.length} nodes`, bg: 'primary' } : null,
                    ].filter(Boolean),
                })}

                {Array.isArray(eventResponse.attack_types) && eventResponse.attack_types.length > 0 && (
                    <div className="d-flex flex-wrap gap-2 mb-3">
                        {eventResponse.attack_types.map((attackType) => (
                            <Badge key={attackType} bg="dark">
                                {attackType}
                            </Badge>
                        ))}
                    </div>
                )}

                <div className="row g-3 mb-3">
                    <div className="col-lg-4">
                        <div className="border rounded bg-body p-3 h-100">
                            <div className="fw-semibold mb-2">Detectie</div>

                            {renderInfoItem('Naam', eventResponse.detection_name || '-')}
                            {renderInfoItem('Bestand', eventResponse.file_name)}
                            {renderInfoItem('Bestandspad', eventResponse.file_path, { code: true })}
                        </div>
                    </div>

                    <div className="col-lg-4">
                        {renderEndpointCard(notification, eventResponse)}
                    </div>

                    <div className="col-lg-4">
                        <div className="border rounded bg-body p-3 h-100">
                            <div className="fw-semibold mb-2">Tijdlijn</div>

                            {renderInfoItem('Aangemaakt', formatEventDate(eventResponse.created || notification.created_at))}
                            {renderInfoItem('Laatste update', formatEventDate(eventResponse.last_updated || notification.updated_at))}
                            {renderInfoItem('Proces', eventResponse.process_path, { code: true })}
                        </div>
                    </div>
                </div>

                {(eventResponse.file_hash_md5 || eventResponse.file_hash_sha256 || eventResponse.att_ck_id?.length > 0) && (
                    <div className="border rounded bg-body p-3 mb-3">
                        <div className="fw-semibold mb-2">Indicators & MITRE ATT&CK</div>

                        <div className="row g-3">
                            <div className="col-lg-6">
                                {renderInfoItem('MD5', eventResponse.file_hash_md5, { code: true })}
                                {renderInfoItem('SHA256', eventResponse.file_hash_sha256, { code: true })}
                            </div>

                            <div className="col-lg-6">
                                {eventResponse.att_ck_id?.length > 0 && (
                                    <>
                                        <div className="text-muted small mb-1">MITRE technieken</div>

                                        <div className="d-flex flex-wrap gap-2">
                                            {eventResponse.att_ck_id.map((id) => (
                                                <Badge key={id} bg="light" text="dark" className="border">
                                                    {id}
                                                </Badge>
                                            ))}
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {nodeGroups.length === 0 && (
                    <div className="text-muted">
                        Geen gestructureerde nodes beschikbaar.
                    </div>
                )}

                {nodeGroups.map((group) => (
                    <details key={group.key} className="mb-3" open={group.defaultOpen}>
                        <summary className="fw-semibold mb-2" style={{ cursor: 'pointer' }}>
                            {group.title} ({group.nodes.length})
                        </summary>

                        <div className="d-flex flex-column gap-2 mt-2">
                            {group.nodes.map((node, index) => renderNodeCard(node, index))}
                        </div>
                    </details>
                ))}

                {renderRawJsonDetails(notification, eventResponse)}
            </div>
        );
    };

    const renderFallbackDetailsPanel = (notification, eventResponse, module) => {
        return (
            <div className="p-3 bg-body-tertiary border-top">
                {renderPanelHeader({
                    title: 'Details beveiligingsmelding',
                    subtitle: `Module: ${translateModule(module || 'Onbekend')}`,
                    notification,
                    eventResponse,
                    badges: [
                        notification.status ? { label: capitalize(notification.status), bg: getStatusBadge(notification.status) } : null,
                    ].filter(Boolean),
                })}

                {renderRawJsonDetails(notification, eventResponse, true)}
            </div>
        );
    };

    const renderEventDetailsPanel = (notification) => {
        const eventResponse = parseEventResponse(notification);
        const module = eventResponse.module || notification.module;

        if (module === 'avc') {
            return renderAvcDetailsPanel(notification, eventResponse);
        }

        if (module === 'av') {
            return renderAvDetailsPanel(notification, eventResponse);
        }

        if (module === 'registration') {
            return renderRegistrationDetailsPanel(notification, eventResponse);
        }

        if (module === 'hwid-change') {
            return renderHwidChangeDetailsPanel(notification, eventResponse);
        }

        if (module === 'modules') {
            return renderModulesDetailsPanel(notification, eventResponse);
        }

        if (module === 'install') {
            return renderInstallDetailsPanel(notification, eventResponse);
        }

        if (module === 'uninstall') {
            return renderUninstallDetailsPanel(notification, eventResponse);
        }

        if (module === 'endpoint-moved-out') {
            return renderEndpointMovedOutDetailsPanel(notification, eventResponse);
        }

        if (module === 'task-status') {
            return renderTaskStatusDetailsPanel(notification, eventResponse);
        }

        if (module === 'aph') {
            return renderAphDetailsPanel(notification, eventResponse);
        }

        if (module === 'uc') {
            return renderUcDetailsPanel(notification, eventResponse);
        }

        if (module === 'fw') {
            return renderFwDetailsPanel(notification, eventResponse);
        }

        if (module === 'new-incident') {
            return renderNewIncidentDetailsPanel(notification, eventResponse);
        }

        return renderFallbackDetailsPanel(notification, eventResponse, module);
    };

    const getRowEventResponse = (notification) => {
        return parseEventResponse(notification);
    };

    if (loading) {
        return <Loader />;
    }

    return (
        <Fragment>
            <ToastContainer position="top-end" className="p-3" style={{ zIndex: 1080 }}>
                <Toast
                    bg={toast.variant}
                    show={toast.show}
                    onClose={() => setToast((current) => ({ ...current, show: false }))}
                    delay={4500}
                    autohide
                >
                    <Toast.Header>
                        <strong className="me-auto">
                            {toast.title || 'Melding'}
                        </strong>
                    </Toast.Header>

                    <Toast.Body className={toast.variant === 'danger' || toast.variant === 'success' ? 'text-white' : ''}>
                        {toast.message}
                    </Toast.Body>
                </Toast>
            </ToastContainer>

            {error && (
                <Alert variant="danger" className="mb-3">
                    {error}
                </Alert>
            )}

            {actionError && (
                <Alert
                    variant="danger"
                    className="mb-3"
                    dismissible
                    onClose={() => setActionError('')}
                >
                    {actionError}
                </Alert>
            )}

            <div className="position-relative">
                {tableLoading && (
                    <div
                        className="position-absolute top-0 start-0 end-0 bottom-0 d-flex align-items-start justify-content-center pt-5 bg-body bg-opacity-75"
                        style={{
                            zIndex: 5,
                        }}
                    >
                        <div className="d-inline-flex align-items-center gap-2 text-muted bg-body border rounded px-3 py-2 shadow-sm">
                            <Spinner animation="border" size="sm" />
                            Notificaties laden...
                        </div>
                    </div>
                )}

                <table className="table table-hover table-bordered align-middle mb-0">
                    <TableHeader
                        columns={columns}
                        filters={filters}
                        onChange={setFilters}
                        onSort={handleSort}
                        sortBy={sortBy}
                        sortDir={sortDir}
                        disabled={tableLoading}
                    />

                    <tbody>
                    {notifications.length === 0 && (
                        <tr>
                            <td colSpan={6} className="text-center text-muted py-4">
                                Geen notificaties gevonden.
                            </td>
                        </tr>
                    )}

                    {notifications.map((notification) => {
                        const eventResponse = getRowEventResponse(notification);
                        const latestQuarantineAction = getLatestAction(notification, 'quarantine_file');
                        const latestUninstallAction = getLatestAction(notification, 'uninstall_endpoint');
                        const latestRemoveQuarantineAction = getLatestAction(notification, 'remove_quarantine_items');

                        return (
                            <Fragment key={notification.id}>
                                <tr
                                    onClick={() =>
                                        setShowDetails(notification.id === showDetails ? null : notification.id)
                                    }
                                    style={{ cursor: 'pointer' }}
                                >
                                    <td className="align-middle">
                                        <div className="d-flex align-items-center">
                                            <div className="avatar avatar-md bg-primary-subtle flex-shrink-0">
                                                <span className="avatar-text rounded-circle text-primary fw-bold">
                                                    {notification.company_name?.[0] || '?'}
                                                </span>
                                            </div>

                                            <div className="ms-2">
                                                <div className="fw-semibold">
                                                    {notification.company_name || '-'}
                                                </div>

                                                <div className="text-muted small">
                                                    {formatTimestamp(notification.created_at)}
                                                </div>
                                            </div>
                                        </div>
                                    </td>

                                    <td>
                                        <div className="fw-semibold">
                                            {translateModule(notification.module || eventResponse.module || '-')}
                                        </div>

                                        {eventResponse.exploit_type && (
                                            <div className="text-muted small">
                                                {eventResponse.exploit_type}
                                            </div>
                                        )}

                                        {eventResponse.detection_name && (
                                            <div className="text-muted small">
                                                {eventResponse.detection_name}
                                            </div>
                                        )}

                                        {eventResponse.malware_name && (
                                            <div className="text-muted small">
                                                {eventResponse.malware_name}
                                            </div>
                                        )}

                                        {eventResponse.product_registration && (
                                            <div className="text-muted small">
                                                {translateStatus(eventResponse.product_registration)}
                                            </div>
                                        )}

                                        {eventResponse.taskName && (
                                            <div className="text-muted small">
                                                {eventResponse.taskName}
                                            </div>
                                        )}

                                        {eventResponse.url && (
                                            <div className="text-muted small text-truncate" style={{ maxWidth: 260 }}>
                                                {eventResponse.url}
                                            </div>
                                        )}

                                        {eventResponse.status && (
                                            <div className="text-muted small">
                                                {translateStatus(eventResponse.status)}
                                            </div>
                                        )}

                                        {eventResponse.old_hwid && eventResponse.new_hwid && (
                                            <div className="text-muted small">
                                                HWID gewijzigd
                                            </div>
                                        )}

                                        {eventResponse.hwid && ['install', 'endpoint-moved-out'].includes(eventResponse.module) && (
                                            <div className="text-muted small">
                                                HWID aanwezig
                                            </div>
                                        )}

                                        {eventResponse.reason !== undefined && eventResponse.module === 'uninstall' && (
                                            <div className="text-muted small">
                                                {translateUninstallReason(eventResponse.reason)}
                                            </div>
                                        )}

                                        {eventResponse.oldData?.features && (
                                            <div className="text-muted small">
                                                {eventResponse.oldData.features.length} features
                                            </div>
                                        )}

                                        {latestQuarantineAction && (
                                            <div className="mt-1">
                                                <Badge
                                                    bg={
                                                        latestQuarantineAction.status === 'success'
                                                            ? 'success'
                                                            : latestQuarantineAction.status === 'failed'
                                                                ? 'danger'
                                                                : 'warning'
                                                    }
                                                >
                                                    {latestQuarantineAction.status === 'success'
                                                        ? 'Quarantaine taak'
                                                        : latestQuarantineAction.status === 'failed'
                                                            ? 'Quarantaine mislukt'
                                                            : 'Quarantaine bezig'}
                                                </Badge>
                                            </div>
                                        )}

                                        {latestUninstallAction && (
                                            <div className="mt-1">
                                                <Badge
                                                    bg={
                                                        latestUninstallAction.status === 'success'
                                                            ? 'success'
                                                            : latestUninstallAction.status === 'failed'
                                                                ? 'danger'
                                                                : 'warning'
                                                    }
                                                >
                                                    {latestUninstallAction.status === 'success'
                                                        ? 'Uninstall taak'
                                                        : latestUninstallAction.status === 'failed'
                                                            ? 'Uninstall mislukt'
                                                            : 'Uninstall bezig'}
                                                </Badge>
                                            </div>
                                        )}

                                        {latestRemoveQuarantineAction && (
                                            <div className="mt-1">
                                                <Badge
                                                    bg={
                                                        latestRemoveQuarantineAction.status === 'success'
                                                            ? 'success'
                                                            : latestRemoveQuarantineAction.status === 'failed'
                                                                ? 'danger'
                                                                : 'warning'
                                                    }
                                                >
                                                    {latestRemoveQuarantineAction.status === 'success'
                                                        ? 'Quarantaine verwijderen'
                                                        : latestRemoveQuarantineAction.status === 'failed'
                                                            ? 'Verwijderen mislukt'
                                                            : 'Verwijderen bezig'}
                                                </Badge>
                                            </div>
                                        )}
                                    </td>

                                    <td className="align-middle">
                                        <div className="fw-semibold">
                                            {notification.computer_name || eventResponse.computer_name || '-'}
                                        </div>

                                        <div className="text-muted small">
                                            {notification.computer_ip || eventResponse.computer_ip || ''}
                                        </div>
                                    </td>

                                    <td>
                                        <Badge bg={getStatusBadge(notification.status)}>
                                            {capitalize(notification.status)}
                                        </Badge>
                                    </td>

                                    <td>{formatTimestamp(notification.created_at)}</td>

                                    <td>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-primary"
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                setShowDetails(
                                                    notification.id === showDetails ? null : notification.id
                                                );
                                            }}
                                        >
                                            {showDetails === notification.id ? 'Verberg details' : 'Toon details'}
                                        </button>
                                    </td>
                                </tr>

                                <tr className={showDetails === notification.id ? '' : 'd-none'}>
                                    <td colSpan={6} className="p-0">
                                        {renderEventDetailsPanel(notification)}
                                    </td>
                                </tr>
                            </Fragment>
                        );
                    })}
                    </tbody>
                </table>
            </div>

            <TableFooter
                totalItems={totalItems}
                page={page}
                pageSize={pageSize}
                onPageChange={setPage}
                onPageSizeChange={handlePageSizeChange}
                pageSizeOptions={[5, 10, 20, 50, 100]}
                loading={tableLoading}
                className="mt-2"
            />
        </Fragment>
    );
};

export default NotificationsComponent;
